/**
 * 지수 백오프(exponential backoff) 기반 재시도 유틸리티
 *
 * 일시적 네트워크 오류, 서버 과부하 등으로 인한 간헐적 실패를 복구하기 위해
 * 점진적으로 대기 시간을 늘려가며 재시도한다.
 */

export interface RetryOptions {
  /** 최대 재시도 횟수 (기본: 3) */
  maxRetries?: number;
  /** 첫 번째 재시도 전 대기 시간 ms (기본: 1000) */
  baseDelay?: number;
  /** 최대 대기 시간 ms (기본: 10000) */
  maxDelay?: number;
  /** 재시도 가능 여부 판별 함수. false를 반환하면 즉시 실패 처리 */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  shouldRetry: () => true,
};

/**
 * 지수 백오프 대기 시간 계산 (jitter 포함)
 * jitter를 추가하여 여러 클라이언트가 동시에 재시도하는 thundering herd 문제 방지
 */
function getBackoffDelay(attempt: number, baseDelay: number, maxDelay: number): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = exponentialDelay * 0.2 * Math.random();
  return Math.min(exponentialDelay + jitter, maxDelay);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 주어진 비동기 함수를 지수 백오프로 재시도
 *
 * @example
 * ```ts
 * const data = await withRetry(() => fetchData(), {
 *   maxRetries: 3,
 *   shouldRetry: (err) => isTransientError(err),
 * });
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: RetryOptions,
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      const isLastAttempt = attempt >= opts.maxRetries;
      if (isLastAttempt || !opts.shouldRetry(error, attempt)) {
        throw error;
      }

      const waitMs = getBackoffDelay(attempt, opts.baseDelay, opts.maxDelay);
      if (import.meta.env.DEV) {
        console.warn(
          `[retry] attempt ${attempt + 1}/${opts.maxRetries} failed, retrying in ${Math.round(waitMs)}ms`,
          error,
        );
      }
      await delay(waitMs);
    }
  }

  throw lastError;
}

/**
 * HTTP 에러가 일시적(transient)인지 판별
 * 네트워크 에러, 타임아웃, 서버 과부하(429/502/503/504) 등
 */
export function isTransientHttpError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const axiosError = error as {
    code?: string;
    response?: { status?: number };
    message?: string;
  };

  // 네트워크 에러 (서버 미응답, DNS 실패 등)
  if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED') {
    return true;
  }

  // 타임아웃
  if (axiosError.message?.includes('timeout')) {
    return true;
  }

  const status = axiosError.response?.status;
  if (!status) return false;

  // 429: Too Many Requests, 502/503/504: 서버 일시 장애
  return status === 429 || status === 502 || status === 503 || status === 504;
}
